/*
| Shared rich tooltip (th-tip). Positioned above the anchor by default, flips
| below when there's no room near the top; the arrow tracks the anchor centre.
| Used by the calendar, attendance and coverage grids.
|
| Robustness (vs. the original teamhero port):
|  - A watchdog drives dismissal from the LIVE :hover state instead of fragile
|    pointerleave timing, so a tip can't linger when its anchor is re-rendered or
|    the cursor jumps away without moving over it.
|  - "Keep open" affordances so you can move into an interactive tip and click:
|    hold Alt/⌥ (transient) or middle-click to lock (CK3-style Action Lock).
|    Escape always dismisses.
|  - Positioning clamps both axes against the viewport using offset sizes
|    (transform-independent), so a right-edge tip no longer bleeds past the edge.
*/
let tipEl: HTMLElement | null = null;
let currentAnchor: HTMLElement | null = null;
let showTimer: number | undefined;
let watchdog: number | undefined;
let missTicks = 0;
let altHeld = false; // Alt/⌥ currently held
let locked = false; // middle-click sticky lock
let globalsInstalled = false;

const TICK = 120; // watchdog cadence; hide after 2 consecutive "not hovered" ticks
const IS_MAC = /Mac|iPhone|iPad/i.test((navigator as any).userAgentData?.platform || navigator.platform || '');
const PIN_GLYPH = IS_MAC ? '⌥' : 'Alt';

const pinned = (): boolean => altHeld || locked;

const installGlobalDismiss = (): void => {
    if (globalsInstalled) return;
    globalsInstalled = true;

    // Scroll / outside press dismiss — unless the tip is held open.
    window.addEventListener('scroll', () => { if (!pinned()) hideTip(); }, true);
    document.addEventListener('pointerdown', (e) => {
        // Middle-click toggles a sticky lock when over the anchor or the tip (CK3 Action Lock).
        if (e.button === 1 && tipEl && overInteractive(e.target)) {
            e.preventDefault();
            locked = !locked;
            tipEl.classList.toggle('th-tip--pinned', pinned());
            return;
        }
        if (tipEl && e.target instanceof Node && tipEl.contains(e.target)) return; // click inside an open tip
        locked = false;
        hideTip();
    }, true);

    // Alt/⌥ held → keep the current tip open and interactive so the cursor can
    // travel into it and click. Escape always dismisses.
    document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape') { locked = false; hideTip(); return; }
        if (e.key === 'Alt' && tipEl) { altHeld = true; tipEl.classList.add('th-tip--pinned'); }
    });
    document.addEventListener('keyup', (e) => {
        if (e.key !== 'Alt') return;
        altHeld = false;
        if (tipEl && !locked) tipEl.classList.remove('th-tip--pinned');
    });
};

const overInteractive = (target: EventTarget | null): boolean =>
    target instanceof Node && ((!!tipEl && tipEl.contains(target)) || (!!currentAnchor && currentAnchor.contains(target)));

const ensure = (): HTMLElement => {
    if (tipEl) return tipEl;
    tipEl = document.createElement('div');
    tipEl.className = 'th-tip';
    tipEl.setAttribute('role', 'tooltip');
    document.body.appendChild(tipEl);
    return tipEl;
};

export function hideTip(): void {
    window.clearTimeout(showTimer);
    window.clearInterval(watchdog);
    watchdog = undefined;
    currentAnchor = null;
    missTicks = 0;
    altHeld = false;
    locked = false;
    if (tipEl) { tipEl.remove(); tipEl = null; }
}

// Watchdog: dismiss from the live hover state. Survives anchor re-render (the
// anchor is no longer :hover / not connected) and static-cursor lingering.
const startWatchdog = (): void => {
    window.clearInterval(watchdog);
    missTicks = 0;
    watchdog = window.setInterval(() => {
        if (!tipEl) { window.clearInterval(watchdog); return; }
        if (pinned()) { missTicks = 0; return; }
        const overAnchor = !!currentAnchor && currentAnchor.isConnected && currentAnchor.matches(':hover');
        const overTip = tipEl.matches(':hover');
        if (overAnchor || overTip) { missTicks = 0; return; }
        if (++missTicks >= 2) hideTip();
    }, TICK);
};

const position = (anchor: HTMLElement): void => {
    if (!tipEl) return;
    const r = anchor.getBoundingClientRect();
    // offsetWidth/Height are the untransformed layout box — stable even while the
    // tip is mid scale-in, so the clamp doesn't under-measure and bleed off-screen.
    const w = tipEl.offsetWidth;
    const h = tipEl.offsetHeight;
    const vw = document.documentElement.clientWidth;
    const vh = document.documentElement.clientHeight;

    let top = r.top - h - 9;
    let flip = false;
    if (top < 8) { top = r.bottom + 9; flip = true; }
    top = Math.max(8, Math.min(top, vh - h - 8));
    const left = Math.max(8, Math.min(r.left + r.width / 2 - w / 2, vw - w - 8));

    tipEl.style.top = `${top}px`;
    tipEl.style.left = `${left}px`;
    tipEl.classList.toggle('th-tip--below', flip);
    const arrowX = Math.max(12, Math.min(r.left + r.width / 2 - left, w - 12));
    tipEl.style.setProperty('--arrow-x', `${arrowX}px`);
};

const show = (anchor: HTMLElement, render: () => string): void => {
    const html = render();
    if (!html) return;
    const el = ensure();
    currentAnchor = anchor;

    const interactive = html.includes('data-tip-href') || html.includes('data-tip-action') || html.includes('<button');
    el.innerHTML = interactive ? html + pinHint() : html;
    el.classList.toggle('th-tip--interactive', interactive);
    el.classList.toggle('th-tip--pinned', pinned());

    // Activate a clicked control: an href navigates; a data-tip-action emits a
    // custom event the caller can listen for. Only bound on interactive tips.
    el.onclick = interactive
        ? (e: MouseEvent): void => {
            const hit = (e.target as HTMLElement).closest<HTMLElement>('[data-tip-href],[data-tip-action]');
            if (!hit) return;
            if (hit.dataset.tipHref) { window.location.href = hit.dataset.tipHref; return; }
            if (hit.dataset.tipAction) {
                document.dispatchEvent(new CustomEvent('tip:action', { detail: { action: hit.dataset.tipAction, anchor } }));
                hideTip();
            }
        }
        : null;

    position(anchor);
    startWatchdog();
    requestAnimationFrame(() => { if (tipEl === el) el.classList.add('th-tip--in'); });
};

const pinHint = (): string =>
    `<span class="tt-pin-hint" aria-hidden="true"><kbd>${PIN_GLYPH}</kbd> přidrž pro akci</span>`;

/**
 * Attach a rich tooltip to an element. `render` returns the inner HTML (the
 * caller is responsible for escaping). Hover opens after 150 ms (instant on
 * focus, or when opts.instant is set); leaving / blurring hides it (unless held
 * open via Alt/⌥ or a middle-click lock).
 */
export const attachTip = (anchor: HTMLElement, render: () => string, opts: { instant?: boolean } = {}): void => {
    installGlobalDismiss();
    const open = (instant: boolean): void => {
        window.clearTimeout(showTimer);
        if (instant) { show(anchor, render); } else { showTimer = window.setTimeout(() => show(anchor, render), 150); }
    };
    anchor.addEventListener('pointerenter', () => open(!!opts.instant));
    anchor.addEventListener('pointerleave', () => { window.clearTimeout(showTimer); }); // watchdog handles the actual hide
    anchor.addEventListener('focus', () => open(true));
    anchor.addEventListener('blur', () => { if (!pinned()) hideTip(); });
};
